'use client';

import { useCallback, useMemo } from 'react';
import qs from 'qs';
import { Button } from '@geberit/gdds';

// types
import type { Platform } from './constants';

// utils
import { useTracking } from 'utils/tracking/track';

type SharingConfig = {
  trackingIcon: string;
  iconSymbol: string;
  params: Record<string, any>;
};

function getSharingConfig(
  symbol: Platform | 'envelope',
  url: string,
  title: string,
  hashtags: string[],
  image?: string | string[],
): SharingConfig | null {
  const baseConfig = {
    trackingIcon: symbol,
    iconSymbol: symbol,
  };

  switch (symbol) {
    case 'facebook':
      return {
        ...baseConfig,
        params: { u: url, hashtags: hashtags.join(',') },
      };
    case 'x':
      return {
        ...baseConfig,
        trackingIcon: 'x',
        params: { text: title, url, hashtags: hashtags.join(',') },
      };
    case 'pinterest':
      return {
        ...baseConfig,
        params: { url, media: image, description: title },
      };
    case 'linkedin':
      return {
        ...baseConfig,
        params: {
          mini: 'true',
          url,
          title,
          summary: `\n${title}\n${url}`,
          provider: url,
        },
      };
    case 'mail':
      return {
        ...baseConfig,
        trackingIcon: 'mail',
        iconSymbol: 'envelope',
        params: { body: `\n\n\n${title}\n${url}` },
      };
    default:
      return null;
  }
}

type SocialShareIconProps = {
  baseUrl: string;
  contentType: 'page' | 'video';
  url: string;
  symbol: Platform | 'envelope';
  image?: string | string[];
  title?: string;
  hashtags?: string[];
  trackingCategory?: string;
  isGddsLightbox?: boolean;
};

export default function SocialShareIcon({
  baseUrl,
  url,
  hashtags = [],
  symbol,
  title = '',
  image,
  contentType,
  isGddsLightbox = false,
}: Readonly<SocialShareIconProps>) {
  const { trackShareAction } = useTracking();

  const sharingConfig = useMemo(
    () => getSharingConfig(symbol, url, title, hashtags, image),
    [symbol, url, title, hashtags, image],
  );

  const sharingBody = useMemo(
    () => (sharingConfig ? qs.stringify(sharingConfig.params) : ''),
    [sharingConfig],
  );

  const link = useMemo(
    () => ({
      target: decodeURIComponent(`${baseUrl}${sharingBody}`),
      window: symbol !== 'mail' ? '_blank' : '',
      rel: 'nofollow',
      buttonStyle: 'flat',
      type: symbol !== 'mail' ? 'external_button' : 'mail_button',
      isIcon: true,
    }),
    [baseUrl, sharingBody, symbol],
  );

  const handleClick = useCallback(() => {
    if (!sharingConfig) return;

    trackShareAction({
      content_type: contentType,
      method: sharingConfig.trackingIcon,
      shareUrl: link.target,
    });

    if (symbol === 'mail') {
      window.location.href = link.target;
    } else {
      window.open(link.target, '_blank', 'nofollow');
    }
  }, [trackShareAction, contentType, sharingConfig, link.target, symbol]);

  if (!sharingConfig) {
    return null;
  }

  return (
    <Button
      width="1.5rem"
      symbol={sharingConfig.iconSymbol}
      stylingType={isGddsLightbox ? 'iconInverted' : 'icon'}
      isIcon
      onClick={handleClick}
    />
  );
}
